import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { Alert } from "react-bootstrap";
import { findMenuBySlugPath } from "./utils/menuSlug";
import NotFound from "./components/NotFound";

const API_URL = process.env.REACT_APP_API_URL;

// =============================================================
// FOOTER INJECTION HELPERS
// =============================================================
const INJECTED_FOOTER_CSS = `
  .injected-app-footer {
    background-color: #1e3a8a;
    color: #ffffff;
    padding: 14px 24px 0;
    margin-top: 40px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  .injected-footer-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 10px;
    width: 95%;
    padding-bottom: 10px;
    align-items: stretch;
  }
  .injected-footer-map-section {
    border-radius: 10px;
    overflow: hidden;
    display: flex;
    align-self: stretch;
    min-height: 120px;
  }
  .injected-footer-map-section iframe {
    width: 100%;
    height: 100%;
    min-height: 120px;
    border: 0;
    display: block;
    flex: 1;
  }
  @media (max-width: 900px) {
    .injected-footer-map-section { min-height: 160px; }
  }
  @media (max-width: 520px) {
    .injected-footer-map-section { min-height: 200px; }
  }
  .injected-footer-section {
    text-align: center;
  }
  .injected-contact-half {
    text-align: left;
  }
  .injected-footer-contact-wide {
    grid-column: span 1;
  }
  .injected-footer-section h3 {
    font-size: 14px;
    font-weight: 700;
    color: #93c5fd;
    margin: 0 0 8px;
    padding-bottom: 6px;
    border-bottom: 1px solid rgba(255,255,255,0.2);
  }
  .injected-footer-section p {
    font-size: 12px;
    line-height: 1.5;
    color: rgba(255,255,255,0.85);
    margin: 0 0 2px;
    justify-content: center;
  }
  .injected-footer-section ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .injected-footer-section ul li {
    margin-bottom: 6px;
  }
  .injected-footer-section ul li a {
    font-size: 13px;
    color: rgba(255,255,255,0.85);
    text-decoration: none;
    cursor: pointer;
  }
  .injected-footer-section ul li a:hover {
    color: #fff;
    text-decoration: underline;
  }
  .injected-contact-split {
    display: flex;
    gap: 0;
    align-items: stretch;
  }
  .injected-contact-half {
    flex: 1;
    padding: 0 16px;
  }
  .injected-contact-divider {
    width: 1px;
    background: rgba(255,255,255,0.25);
    align-self: stretch;
  }
  @media (max-width: 900px) {
    .injected-contact-split {
      flex-direction: column;
    }
    .injected-contact-divider {
      width: 100%;
      height: 1px;
      margin: 12px 0;
    }
    .injected-contact-half {
      padding: 0;
    }
  }
  .injected-footer-social {
    display: flex;
    gap: 5px;
    margin-top: 8px;
    flex-wrap: nowrap;
    justify-content: center;
  }
  .injected-footer-social a {
    width: 32px;
    height: 32px;
    background: rgba(255,255,255,0.12);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    text-decoration: none;
    font-size: 16px;
    transition: background 0.3s, color 0.3s, transform 0.2s;
  }
  .injected-footer-social a:hover {
    transform: translateY(-3px);
  }
  .injected-footer-social a[aria-label="Facebook"]:hover {
    background: #1877F2;
    color: #fff;
  }
  .injected-footer-social a[aria-label="Twitter"]:hover {
    background: #000;
    color: #fff;
  }
  .injected-footer-social a[aria-label="LinkedIn"]:hover {
    background: #0A66C2;
    color: #fff;
  }
  .injected-footer-social a[aria-label="Instagram"]:hover {
    background: radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%);
    color: #fff;
  }
  .injected-footer-social a[aria-label="YouTube"]:hover {
    background: #FF0000;
    color: #fff;
  }
  .injected-footer-bottom {
    border-top: 1px solid rgba(255,255,255,0.15);
    padding: 6px 0;
    text-align: center;
    font-size: 11px;
    color: rgba(255,255,255,0.65);
    width: 100%;
  }
  .injected-footer-bottom a {
    color: rgba(255,255,255,0.65);
    text-decoration: none;
  }
  .injected-footer-bottom a:hover { color: #fff; }
  @media (max-width: 900px) {
    .injected-app-footer { padding: 24px 0 0; width: 100%; }
    .injected-footer-grid { grid-template-columns: 1fr; width: 100%; padding: 0 16px 16px; box-sizing: border-box; }
    .injected-footer-contact-wide { grid-column: span 1; }
    .injected-footer-social { padding-right: 50px; }
  }
`;

const INJECTED_FOOTER_HTML = `
  <footer class="injected-app-footer">
    <div class="injected-footer-grid">

      <!-- COL 1: Find Us -->
      <div class="injected-footer-section" style="text-align:left;">
        <h3>Find Us</h3>
        <p><i class="fas fa-map-marker-alt" style="margin-right:8px"></i>Shukla Medical<br>
          <span style="margin-left:24px">8300 Sheen Drive<br></span>
          <span style="margin-left:24px">St. Petersburg, FL 33709 USA</span></p>
      </div>

      <!-- COL 2: Shukla Medical -->
      <div class="injected-footer-section" style="text-align:left; border-right:1px solid rgba(255,255,255,0.25); padding-right:16px;">
        <h3>Shukla Medical</h3>
        <p><i class="fas fa-phone-volume" style="margin-right:6px"></i>888-4-SHUKLA</p>
        <p><i class="fas fa-phone" style="margin-right:6px"></i>(888-474-8552) &nbsp;<i class="fas fa-fax" style="margin-left:6px; margin-right:6px"></i>727-626-2770</p>
        <p><i class="fas fa-envelope" style="margin-right:6px"></i>cs@shuklamedical.com</p>
      </div>

      <!-- COL 3: Shukla Surgical Support -->
      <div class="injected-footer-section" style="text-align:left; padding-left:16px;">
        <h3>Shukla Surgical Support</h3>
        <p><i class="fas fa-phone-volume" style="margin-right:6px"></i>888-4-SHUKLA</p>
        <p><i class="fas fa-phone" style="margin-right:6px"></i>(888-474-8552) &nbsp;<i class="fas fa-fax" style="margin-left:6px; margin-right:6px"></i>727-626-2770</p>
        <p><i class="fas fa-envelope" style="margin-right:6px"></i>pps@shuklasurgicalsupport.com</p>
      </div>

      <!-- COL 4: Connect With Us -->
      <div class="injected-footer-section">
        <h3>Connect With Us</h3>
        <p>Follow us on social media for updates</p>
        <div class="injected-footer-social">
          <a href="https://www.facebook.com/xtractall/" aria-label="Facebook" target="_blank" rel="noopener noreferrer"><i class="fab fa-facebook-f"></i></a>
          <a href="https://x.com/ShuklaMedical" aria-label="Twitter" target="_blank" rel="noopener noreferrer"><i class="fab fa-twitter"></i></a>
          <a href="https://www.linkedin.com/company/shukla-medical/" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer"><i class="fab fa-linkedin-in"></i></a>
          <a href="https://www.instagram.com/shuklamedical/" aria-label="Instagram" target="_blank" rel="noopener noreferrer"><i class="fab fa-instagram"></i></a>
          <a href="https://www.youtube.com/channel/UCGwMABEqQLERKURHwuH7R8w" aria-label="YouTube" target="_blank" rel="noopener noreferrer"><i class="fab fa-youtube"></i></a>
        </div>
      </div>

    </div>
    <div class="injected-footer-bottom">
      <p style="margin-bottom:4px;">
        <a href="#" data-menu-id="63">Team Contact List</a> &nbsp;|&nbsp;
        <a href="#" data-menu-id="3">Quality Care</a> &nbsp;|&nbsp;
        <a href="#" data-menu-id="5">Medical Professionals</a> &nbsp;|&nbsp;
        <a href="#privacy">Privacy Policy</a> &nbsp;|&nbsp;
        <a href="#terms">Terms of Service</a>
      </p>
      <p>&copy; 2024 Shukla Medical. All Rights Reserved.</p>
    </div>
  </footer>
`;

// Map of menuIds that should open a PDF in a new tab
const pdfMenuMap = {
  9: '/Documents/SHUKLA-Systems-All-in-One-Brochure-2026.pdf',
  11: '/Documents/SpecialOrderPartsBrochure2025.pdf'
};

// =============================================================
// ULTRA-WIDE ZOOM CAP — REMOVED
// =============================================================
// An injected script used to re-cap every marketing page to a 1400px canvas
// (zoomed by height, centered) once the window ratio passed ~16:9. With the
// full-width redesign that cap became the thing CREATING left/right gaps on
// wide monitors, and it silently overrode the pages' own responsive layout.
// The stored pages now govern themselves: the home page's #canvas script
// scales by width (edge-to-edge at any ratio) and every other page is plain
// responsive full-width HTML.

// Hard-coded root-home menuId. The "Home" row in the Menu table seeds at
// MenuId = 1 (see migration/02_data.sql), and the same constant is used by
// the navbar/Login back-link to mean "the landing page". Centralizing it
// here keeps the bare-/home behaviour together with the rest of Sidebar.
const ROOT_HOME_MENU_ID = '1';

function Sidebar() {
  // The route is /home/* — params['*'] is everything after /home/. It can be:
  //   - empty           → bare /home, render the landing page
  //   - "1"             → legacy numeric menuId (kept working for old bookmarks)
  //   - "products/shukla-hip/blade" → hierarchical slug path
  const params = useParams();
  const rawPath = params['*'] || '';
  const { menuData = [] } = useOutletContext() || {};
  const navigate = useNavigate();

  // Resolve whichever URL form arrived into the numeric menuId the rest of
  // the component (PDF map, MenuHtml fetch) is built around. Bare /home
  // shows the landing page so the URL stays clean — no /home/1 / /home/home.
  const menuId = (() => {
    if (!rawPath) return ROOT_HOME_MENU_ID;
    if (/^\d+$/.test(rawPath)) return rawPath; // legacy numeric
    const menu = findMenuBySlugPath(menuData, rawPath);
    return menu ? String(menu.menuId) : null;
  })();

  const [iframeKey, setIframeKey] = useState(0);
  const [error, setError] = useState(null);
  // The menu exists but has no page content (MenuHtml answers 404).
  const [noContent, setNoContent] = useState(false);
  const [iframeHtml, setIframeHtml] = useState('');
  const iframeRef = useRef(null);
  // The PDF menu already opened for this visit. The fetch effect below runs again when
  // iframeKey bumps right after mount, which opened the brochure twice and stepped back two
  // pages (UI-PUB-007); this keeps it to once per visit.
  const pdfOpenedFor = useRef(null);

  // =========================
  // INJECT FOOTER INTO IFRAME
  // =========================
  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const doc = iframe.contentDocument;

    if (doc && doc.body) {
      doc.querySelector('.injected-app-footer')?.remove();
      doc.querySelector('style[data-injected-footer]')?.remove();

      if (!doc.querySelector('link[href*="font-awesome"]')) {
        const faLink = doc.createElement('link');
        faLink.rel = 'stylesheet';
        faLink.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
        doc.head.appendChild(faLink);
      }

      const style = doc.createElement('style');
      style.setAttribute('data-injected-footer', 'true');
      style.textContent = INJECTED_FOOTER_CSS;
      doc.head.appendChild(style);

      const temp = doc.createElement('div');
      temp.innerHTML = INJECTED_FOOTER_HTML;
      const footerTarget = doc.querySelector('#canvas .site-wrapper')
        || doc.querySelector('#canvas')
        || doc.body;
      footerTarget.appendChild(temp.firstElementChild);

      doc.querySelectorAll('[data-menu-id]').forEach(el => {
        el.addEventListener('click', e => {
          e.preventDefault();
          window.parent.postMessage(
            { type: 'NAVIGATE_MENU', menuId: parseInt(el.getAttribute('data-menu-id'), 10) },
            '*'
          );
        });
      });
    } else {
      iframe.contentWindow?.postMessage(
        { type: 'INJECT_FOOTER', footerHtml: INJECTED_FOOTER_HTML, footerCss: INJECTED_FOOTER_CSS },
        '*'
      );
    }
  };

  // =========================
  // RELOAD ON MENU CHANGE
  // =========================
  useEffect(() => {
    setIframeKey(prev => prev + 1);
  }, [menuId]);

  // =========================
  // FETCH HTML
  // =========================
  useEffect(() => {
    if (!menuId) return;

    // PDF in new tab - once per visit, then one step back.
    if (pdfMenuMap[Number(menuId)]) {
      if (pdfOpenedFor.current !== menuId) {
        pdfOpenedFor.current = menuId;
        window.open(pdfMenuMap[Number(menuId)], '_blank');
        navigate(-1);
      }
      return;
    }
    pdfOpenedFor.current = null;

    setIframeHtml('');
    setNoContent(false);
    setError(null);
    const UI_ORIGIN = window.location.origin;

    // no-store: page HTML is edited in the DB (SmstaticData); without it the
    // browser heuristically caches the response and content edits don't show
    // until a hard refresh.
    fetch(`${API_URL}/api/Common/MenuHtml/${menuId}`, { cache: 'no-store' })
      .then(r => {
        if (r.status === 404) {
          setNoContent(true);
          return null;
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then(html => {
        if (html === null) return;
        const processed = html.replace(/(['"(])\/Images\//g, `$1${UI_ORIGIN}/Images/`);
        setIframeHtml(processed);
      })
      .catch(err => {
        console.error('Failed to load menu HTML:', err);
        setError(err.message);
      });
  }, [menuId, iframeKey, navigate]);

  // =========================
  // RENDER
  // =========================
  // UI-PUB-003 / UI-PUB-006 (2026-10-22): an address no public menu matches, or a menu whose page
  // has no content, showed only the navbar over a white page. Show the app's "Page not found" card
  // instead - once the menu list has arrived, so it never flashes while the menu is still loading.
  if ((!menuId && menuData.length > 0) || noContent) {
    return <NotFound />;
  }

  return (
    <div style={{ height: "100vh" }}>
      {error && !menuId && (
        <div style={{ padding: "20px", textAlign: "center" }}>
          <Alert variant="danger">
            <Alert.Heading>Connection Error</Alert.Heading>
            <p>{error}</p>
            <hr />
            <p className="mb-0">
              Please ensure the API server is running at {API_URL}
            </p>
          </Alert>
        </div>
      )}

      {menuId && iframeHtml && (
        <div style={{ overflow: "hidden", height: "100%" }}>
          <iframe
            ref={iframeRef}
            key={iframeKey}
            srcDoc={iframeHtml}
            title="Menu Content"
            onLoad={handleIframeLoad}
            style={{
              width: "calc(100% + 20px)",
              height: "100%",
              border: "none"
            }}
          />
        </div>
      )}
    </div>
  );
}

export default Sidebar;
